import React from "react";
import { Box, Typography } from "@mui/material";

// ─── UTILITY FUNCTIONS ───────────────────────────────────────────────────────

/**
 * Dynamic axis bounds with 5-unit breathing room.
 * axisMax = ceil(dataMax to nearest 5), hard capped at 100
 * axisMin = floor(dataMin to nearest 5), hard capped at 0
 *
 * Examples:
 *   dataMax=16.9 → axisMax=20   dataMax=100 → axisMax=100
 *   dataMin=8.7  → axisMin=5    dataMin=0   → axisMin=0
 */
const getAxisBounds = (breakdown) => {
    const percents = breakdown.map((i) => i.percentage);
    const dataMax = Math.max(...percents);
    const dataMin = Math.min(...percents);
    const axisMax = Math.min(100, Math.ceil(dataMax / 5) * 5);
    const axisMin = Math.max(0, Math.floor(dataMin / 5) * 5);
    return { axisMax, axisMin };
};

/**
 * Converts a data percent → CSS bar width (0–100%).
 * Divide-by-zero safe: all equal values → 100%.
 * Hard clamped: never below 0, never above 100.
 */
const getBarWidth = (percent, axisMin, axisMax) => {
    if (axisMax === axisMin) return 100;
    const relative = ((percent - axisMin) / (axisMax - axisMin)) * 100;
    return Math.min(100, Math.max(0, relative));
};

// ─────────────────────────────────────────────────────────────────────────────

const PortfolioProgressBar = ({ breakdown = [] }) => {

    // Normalize API keys + clamp percentage to 0–100
    const validBreakdown = breakdown
        .map((item) => ({
            ...item,
            percentage: Math.min(100, Math.max(0,
                parseFloat(item.percentage ?? item.percent ?? 0) || 0
            )),
            label: item.label ?? item.type ?? "Unknown",
            color: item.color ?? "#7B5EA7",
        }));

    // Simplified max for normalization
    const maxPercent = Math.max(...validBreakdown.map(i => i.percentage), 1);
    const axisMax = Math.min(100, Math.ceil(maxPercent / 5) * 5);

    const leftItems = validBreakdown.slice(0, 4);
    const rightItems = validBreakdown.slice(4);

    /**
     * Renders a column of bar rows.
     * Each column uses a 3-column grid for consistent alignment:
     * [Label] [Bar Cell] [Percentage]
     */
    const BarColumn = ({ items }) => (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: {
                    xs: "minmax(80px, 1fr) 2fr minmax(40px, auto)",
                    sm: "minmax(60px, auto) 140px 45px"
                },
                alignItems: "center",
                columnGap: { xs: "8px", sm: "16px" },
                rowGap: "10px",
                mb: { xs: 2, sm: 1 },
                width: "100%"
            }}
        >
            {items.map((item) => (
                <React.Fragment key={item.id ?? item.label}>

                    {/* Label */}
                    <Typography
                        sx={{
                            fontSize: "11px",
                            color: "rgba(255, 255, 255, 0.55)",
                            whiteSpace: "nowrap",
                            textAlign: { xs: "left", sm: "right" },
                        }}
                    >
                        {item.label}
                    </Typography>

                    {/* Bar Container */}
                    <Box sx={{ width: "100%", position: "relative" }}>
                        <Box
                            sx={{
                                width: `${(item.percentage / axisMax) * 100}%`,
                                height: 5,
                                backgroundColor: item.color,
                                borderRadius: "100px",
                                transition: "width 0.5s ease",
                            }}
                        />
                    </Box>

                    {/* Percentage */}
                    <Typography
                        sx={{
                            fontSize: "10.5px",
                            color: "rgba(255, 255, 255, 0.55)",
                            whiteSpace: "nowrap",
                            fontWeight: 500,
                            textAlign: "right"
                        }}
                    >
                        {item.percentage}%
                    </Typography>

                </React.Fragment>
            ))}
        </Box>
    );

    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: {
                    xs: "1fr",
                    sm: rightItems.length > 0 ? "auto auto" : "auto"
                },
                columnGap: "32px",
                alignItems: "start",
                width: "100%"
            }}
        >
            <BarColumn items={leftItems} />
            {rightItems.length > 0 && <BarColumn items={rightItems} />}
        </Box>
    );
};

export default PortfolioProgressBar;